import { useState } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useRouter } from 'next/router';
import { addCommas, removeCommas } from 'lib/utils/jobs';
import { FilterPopover } from 'components/FilterPopover';
import { clearQuery, updateRouterQuery } from 'lib/utils/filterUtils';
import { jobPayTypes } from 'lib/constants/jobs';
import { JobPayType } from 'lib/types/jobs';

interface Props {
  payMin?: number;
  payType?: JobPayType;
}

export const PayFilter = ({
  payMin = 0, payType,
}: Props) => {
  const router = useRouter();
  const allTypes = Object.keys(jobPayTypes);

  const [_payMin, setPayMin] = useState<string|undefined>(addCommas(payMin));
  const [_payType, setPayType] = useState<string|undefined>(payType);

  const onApply = () => {
    const num = removeCommas(_payMin);

    if (Number.isNaN(num)) {
      return;
    }

    if (num === Number(payMin) && payType === _payType) {
      return;
    }

    /* Clear the query if the value is empty */
    if (!_payMin) {
      updateRouterQuery(router, { payMin: [], payType: [] });
      return;
    }

    const query = {
      ...clearQuery(['payMin', 'payType']),
      payMin: num.toString(),
      payType: _payType,
    };
    updateRouterQuery(router, query);
  };

  const onClear = () => {
    updateRouterQuery(router, { payMin: [], payType: [] });
    setPayMin(undefined);
    setPayType(undefined);
  };

  const getTitle = () => {
    if (!_payMin) {
      return 'Pay';
    }

    if (!_payType) {
      return `Pay at least: ${_payMin}`;
    }

    return `Pay at least: ${_payMin}${jobPayTypes[_payType as JobPayType].per}`;
  };

  return (
    <FilterPopover
      title={getTitle()}
      onApply={onApply}
      onClear={onClear}
      clearable={!!_payMin || !!_payType}
    >
      <div className="flex gap-4">
        <div className="w-full md:w-1/2">
          <TextField
            id="payMin"
            fullWidth
            size="small"
            label="minimum"
            inputProps={{
              maxLength: 9,
            }}
            value={_payMin}
            onChange={(e) => setPayMin(addCommas(e.target.value))}
          />
        </div>
        <div className="w-full md:w-1/2">
          <FormControl className="w-full" size="small">
            <InputLabel id="payTypeLabel">Rate</InputLabel>
            <Select
              className="w-full"
              label="per"
              labelId="payTypeLabel"
              id="payType"
              onChange={(e) => setPayType(e.target.value)}
              value={_payType}
            >
              <MenuItem value="">All</MenuItem>
              {allTypes.map(
                (key: string) => (
                  <MenuItem key={key} value={key}>
                    {jobPayTypes[key as JobPayType].label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </div>

      </div>
    </FilterPopover>
  );
};
